import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, getStoredAuthHash, acceptInviteByToken } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';

export function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, setSession, fetchProfile } = useAuthStore();
  const hasProcessed = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double-processing
      if (hasProcessed.current) return;
      hasProcessed.current = true;

      // Get hash from storage (set before redirect in lib/supabase.ts)
      const storedHash = getStoredAuthHash();
      const hash = storedHash || window.location.hash;

      try {
        // Check for hash fragment (email change, signup verification, etc.)
        if (hash && hash.includes('access_token')) {
          const params = new URLSearchParams(hash.substring(1));
          const type = params.get('type');
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');

          // Clear hash from URL if present
          if (window.location.hash) {
            window.history.replaceState(null, '', window.location.pathname);
          }
          
          if (accessToken && refreshToken) {
            // Set session and wait for it
            const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (sessionError) {
              console.error('Session error:', sessionError);
              navigate('/login', { replace: true });
              return;
            }

            if (type === 'email_change') {
              sessionStorage.setItem('auth_message', 'Email address successfully updated');
              navigate('/overview', { replace: true });
              return;
            }

            // For signup verification, check onboarding status
            if (sessionData.session) {
              setUser(sessionData.session.user);
              setSession(sessionData.session);
              await fetchProfile(sessionData.session.user.id);

              // Check if user needs onboarding
              const { data: profile } = await supabase
                .from('profiles')
                .select('onboarding_completed')
                .eq('id', sessionData.session.user.id)
                .single();

              const inviteResult = await acceptInviteByToken(sessionData.session.user.id);
              if (inviteResult) {
                await fetchProfile(sessionData.session.user.id);
                navigate('/overview', { replace: true });
              } else if (!profile?.onboarding_completed) {
                navigate('/onboarding/step-1', { replace: true });
              } else {
                navigate('/overview', { replace: true });
              }
              return;
            }
          }
        }

        // Get the code from URL (PKCE flow - OAuth)
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');

        if (code) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          
          if (error) {
            console.error('Code exchange error:', error);
            navigate('/login', { replace: true });
            return;
          }

          if (data.session) {
            setUser(data.session.user);
            setSession(data.session);
            await updateProfileFromOAuth(data.session.user);
            await fetchProfile(data.session.user.id);

            const { data: profile } = await supabase
              .from('profiles')
              .select('onboarding_completed')
              .eq('id', data.session.user.id)
              .single();

            const inviteResult = await acceptInviteByToken(data.session.user.id);
            if (inviteResult) {
              await fetchProfile(data.session.user.id);
              navigate('/overview', { replace: true });
            } else if (!profile?.onboarding_completed) {
              navigate('/onboarding/step-1', { replace: true });
            } else {
              navigate('/overview', { replace: true });
            }
            return;
          }
        }

        // Fallback: Check for existing session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session error:', error);
          navigate('/login', { replace: true });
          return;
        }

        if (session) {
          setUser(session.user);
          setSession(session);
          await updateProfileFromOAuth(session.user);
          await fetchProfile(session.user.id);
          const inviteResult = await acceptInviteByToken(session.user.id);
          if (inviteResult) await fetchProfile(session.user.id);
          navigate('/overview', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        navigate('/login', { replace: true });
      }
    };

    handleCallback();
  }, [navigate, setUser, setSession, fetchProfile]);

  return (
    <div className="loading-screen">
      <div className="loading-spinner" />
      <div className="spacer-small"></div>
      <div className="text-size-regular text-colour-alt-grey">
        Completing sign in...
      </div>
    </div>
  );
}

type OAuthIdentity = {
  identity_data?: Record<string, unknown> | null;
};

type OAuthUser = {
  id: string;
  email?: string;
  user_metadata?: Record<string, unknown>;
  identities?: OAuthIdentity[];
};

function getMetadataValue(sources: Record<string, unknown>[], keys: string[]) {
  for (const source of sources) {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
  }

  return '';
}

function splitFullName(fullName: string) {
  const parts = fullName.trim().split(/\s+/);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
}

// Helper to update profile from OAuth metadata
async function updateProfileFromOAuth(user: OAuthUser) {
  const metadataSources = [
    user.user_metadata || {},
    ...(user.identities || []).map((identity) => identity.identity_data || {}),
  ];

  let firstName = getMetadataValue(metadataSources, ['first_name', 'given_name']);
  let lastName = getMetadataValue(metadataSources, ['last_name', 'family_name']);

  if (!firstName && !lastName) {
    const fullName = getMetadataValue(metadataSources, ['full_name', 'name']);
    if (fullName) {
      const parsedName = splitFullName(fullName);
      firstName = parsedName.firstName;
      lastName = parsedName.lastName;
    }
  }

  const avatarUrl = getMetadataValue(metadataSources, ['avatar_url', 'picture']) || null;

  const { data: existingProfile, error: profileError } = await supabase
    .from('profiles')
    .select('id,email,first_name,last_name,avatar_url')
    .eq('id', user.id)
    .maybeSingle();

  if (profileError) {
    console.error('Error fetching OAuth profile:', profileError);
    return;
  }

  if (!existingProfile) {
    const { error: insertError } = await supabase.from('profiles').insert({
      id: user.id,
      email: user.email || null,
      first_name: firstName || null,
      last_name: lastName || null,
      avatar_url: avatarUrl,
    });

    if (insertError) {
      console.error('Error creating OAuth profile:', insertError);
    }

    return;
  }

  const updateData: Record<string, string | null> = {};
  if (user.email && existingProfile.email !== user.email) updateData.email = user.email;
  if (firstName && !existingProfile.first_name) updateData.first_name = firstName;
  if (lastName && !existingProfile.last_name) updateData.last_name = lastName;
  if (avatarUrl && !existingProfile.avatar_url) updateData.avatar_url = avatarUrl;

  if (Object.keys(updateData).length > 0) {
    const { error: updateError } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', user.id);

    if (updateError) {
      console.error('Error updating OAuth profile:', updateError);
    }
  }
}
